<script context='module'>

  /**
   * @author M.G.
   */

  import EmailEditor from './EmailEditor.svelte';
  import {External, M} from '../../external';

  export function editEmail(contact, data, onUpdate, showDate) {

    if (!contact.subscribed) {
      External.Tools.showError(M.CONTACT_NOT_SUBSCRIBED);
      return;
    }

    return new Promise((resolve) => {
      let component;
      External.Tools.showWindow({
        title: '',
        icon: External.Popup.ICON_EDIT,
        width: 650,
        addCloseButton: true,
        height: (window.innerHeight * 0.7) + 'px',
        customClass: 'contact-email-dialog2',
        isModal: true,
        withDarkBackground: true,
        forceAtRight: false,//External.showBuilderPopupsOnFullHeight,
        action: {
          onShow: (popup, context) => {
            component = new EmailEditor({
              target: popup.getContentArea(),
              props: {
                contact: contact,
                popup: popup,
                data: data,
                onUpdate: onUpdate,
                showDate: showDate
              }
            });
            resolve(component);
          },
          onHide: () => {
            if (component) {
              component.$destroy();
              component = null;
            }
          }
        }
      });
    });
  }

</script>

<script>

  import EmailBuilder from './EmailBuilder.svelte';
  import {getScheduledEmailContent, showScheduledEmailList} from './ScheduledEmailPicker.svelte';
  import DateTimePicker from '../../Common/DateTimePicker.svelte';
  import {sendTestEmail} from './EmailBuilder.svelte';
  import {screenshot} from '../../Common/tools';
  import {onDestroy} from 'svelte';

  export let popup, contact, data, onUpdate, showDate;

  let jTextArea, emailBuilder, datePicker, editorText = '', builderContent = null;  //separate tmp values for builderContent and editorText allows us to restore the editor text if the builder content is discarded
  let replyToHidden = true;

  const TYPE_TEXT = 1, TYPE_BUILDER = 2;

  if (data) {
    setEditorData();
  } else {
    data = {
      contactId: contact.id,
      email: contact.email,
      replyTo: contact.email,
      subject: '',
      name: '',
      fromEmail: '',
      content: null,
      execDate: null,
      emailId: null,
      type: 0,
      thumb: null
    };

    if (External.Website.emailMarketingSettings) {
      data.name = External.Website.emailMarketingSettings.defaultSender || '';
      data.fromEmail = External.Website.emailMarketingSettings.defaultEmail || '';
      data.replyTo = data.fromEmail;
    }

    if (showDate) {
      showDatePicker();
    }
  }

  function addProperty() {
    External.UITools.selectMarketingProperty(null, (tag) => {
      data.subject = (data.subject || '') + '{' + tag + '}';
    });
  }

  async function showBuilder() {

    data.content = builderContent;

    External.Tools.showWindow({
      icon: External.Popup.ICON_EDIT,
      width: window.innerWidth * 0.9,
      customClass: 'automation-step-email-builder',
      isModal: false,
      withDarkBackground: true,
      noTitle: true,
      forceAtRight: true,

      markup: '<div class="full-size flex flex-col f-left bind-component"></div>',
      action: {
        onShow: (popup, context) => {
          emailBuilder = new EmailBuilder({
            target: context.find('.bind-component')[0],
            props: {
              data: data,
              onSaveEmail: (emailData, closePopup) => {
                builderContent = emailData.content;
                showBuilderThumb();
                jTextArea.val(editorText = '');
                if (closePopup) {
                  popup.close();
                }
              },
              onClose: () => {
                popup.close();
              }
            }
          });
        },
        onHide: () => {
          emailBuilder.$destroy();
          emailBuilder = null;
        }
      }
    });
  }

  function showBuilderThumb() {
    screenshot({
      content: builderContent,
      dimensions: builderContent.style.width,
      onReady: (filename) => {
        data.thumb = External.getThumbnailsFromBase(window.getUserDir(), 'emails', false) + filename + '.png?v=' + External.Tools.getTimestamp();
      }
    });
  }

  function discardEmail() {
    External.Tools.prompt(M.DISCARD_DESIGN, M.DISCARD_DESIGN_DESC, () => {
      builderContent = null;
      initTextArea();
    });
  }

  function clearEditor() {
    data.subject = '';
    data.content = null;
    data.execDate = null;
    data.emailId = null;
    data.thumb = null;
    builderContent = null;
    setTextAreaValue(editorText = '');
    datePicker.value = '';
  }

  function sendTest() {
    try {
      validateData();
    } catch (ex) {
      External.Tools.showError(ex);
      return;
    }
    sendTestEmail(data);
  }

  function sendNow() {
    confirmSend('sendIndividualEmail', M.SEND_EMAIL, M.CONFIRM_SEND_EMAIL_DESC, M.EMAIL_SENT);
  }

  function scheduleEmail() {
    if (data.execDate.getTime() <= new Date().getTime()) {
      sendScheduledNow();
      return;
    }

    let dateFormat = External.WebsiteSettings.getDateFormat();

    let prompt = M.CONFIRM_SCHEDULE_EMAIL_DESC
      .replace('01/01/1970',
        External.Tools.dateToStr(data.execDate, dateFormat))
      .replace('00:00',
        External.Tools.timeToString(data.execDate, dateFormat === 'uk'));

    if (data.emailId) {                                                                                   //existing scheduled email
      confirmSend('updateScheduledEmail', M.UPDATE_EMAIL, prompt, M.SCHEDULED_EMAIL_UPDATED);
    } else {                                                                                              //else create new scheduled email
      confirmSend('scheduleIndividualEmail', M.SCHEDULE_EMAIL, prompt, M.EMAIL_SUCCESSFULLY_SCHEDULED);
    }
  }

  function sendScheduledNow() {
    confirmSend('sendScheduledEmail', M.SEND_EMAIL, M.CONFIRM_SEND_EMAIL_DESC, M.EMAIL_SENT);
  }

  function confirmSend(requestAction, promptHeading, promptDesc, successMsg) {
    try {
      validateData();
    } catch (ex) {
      External.Tools.showError(ex);
      return;
    }

    External.Tools.prompt(promptHeading, promptDesc, () => {
      External.Tools.makeWebsiteRequest({
        action: requestAction,
        method: 'post',
        data: {
          data: JSON.stringify(data)
        },
        onOk: () => {
          External.Tools.showAutohideMessage(5, successMsg);
          if (onUpdate) {
            onUpdate();
          }
          if (popup) {
            popup.close();
          }
        }
      });
    });
  }

  function validateData() {
    data.name = data.name.trim();
    data.fromEmail = data.fromEmail.trim();
    data.replyTo = data.replyTo.trim();
    data.subject = data.subject.trim();

    if (!data.name.length) {
      throw M.INVALID_SENDER_NAME;
    }
    if (!External.Tools.isEmailValid(data.fromEmail)) {
      throw M.INVALID_FROM_EMAIL;
    }
    if (!data.subject.length) {
      throw M.INVALID_SUBJECT;
    }

    if (!External.Tools.isEmailValid(data.replyTo)) {
      data.replyTo = data.fromEmail;
    }

    if (builderContent && builderContent.content.length) {
      data.content = builderContent;
      data.type = TYPE_BUILDER;
    } else {

      editorText = jTextArea.val().trim();

      if (stripMarkup(editorText).length) {
        data.content = applyTextTemplate(editorText);
        data.type = TYPE_TEXT;
      } else {
        throw M.INVALID_MESSAGE;
      }
    }
  }

  function stripMarkup(str) {
    return str.replace(/<[^>]*>/g, ' ').replace(/\s{2,}/g, ' ').trim();
  }

  function cancel() {
    if (builderContent || (jTextArea && jTextArea.val().trim().length)) {
      if (!data.emailId) {
        External.Tools.prompt(M.DISCARD_EMAIL, M.DISCARD_EMAIL_DESC, () => {
          popup.close();
        });
        return;
      }
    }
    popup.close();
  }

  function initTextArea(node) {
    if (node) {
      jTextArea = External.$(node).find('textarea');
    }
    External.Tools.addTextEditor({
      element: jTextArea,
      addLinkButton: true,
      hasPersonalizationTags: true,
      isMinimalistic: true,
      addExtraButtons: false,
      addImageButton: false,
      addMarketingTags: true,
      useEmailFontsOnly: true,
      isEmailEditor: true,
      customClass: 'individual-email-editor'
    });
  }

  function setTextAreaValue(val) {
    jTextArea.val(val);
    External.Tools.removeTextEditor(jTextArea);
    initTextArea();
  }

  function applyTextTemplate(text) {
    return {
      isTextEmail: true,
      content: [
        {content: text}
      ],
      style: {
        backgroundColor: '#FFF',
        width: '680px',
        fontFamily: 'Arial'
      }
    };
  }

  function showDatePicker() {
    if (!data.execDate) {
      let date = new Date();
      date.setDate(date.getDate() + 1);
      data.execDate = date;
      External.$('#datePicker').click();
    }
  }

  function hideDatePicker() {
    data.execDate = null;
  }

  function dateChanged(value) {
    data.execDate = new Date(value);
  }

  async function showScheduledEmails() {
    await showScheduledEmailList(contact, setEditorData, clearEditor, () => {
      External.Tools.prompt(M.SCHEDULED_EMAILS, M.NO_EMAILS_SCHEDULED, () => {
        showDatePicker();
      });
    });
  }

  export async function setEditorData(emailData) {
    if (emailData) {
      data = emailData;
    }

    data.content = await getScheduledEmailContent(data.contactId, data.emailId);

    if ((!data.content) || !data.content.content.length) {
      External.Tools.showError(M.NO_EMAIL_DATA_FOUND);
      return false;
    }

    if (data.type === TYPE_TEXT) {
      if (editorText !== data.content.content[0].content) {
        editorText = data.content.content[0].content;
        builderContent = null;
      }
    } else if (data.type === TYPE_BUILDER) {
      if (builderContent !== data.content) {
        builderContent = data.content;
        editorText = '';
      }
    } else {
      External.Tools.showError(M.ERR_UNDEFINED);
      return false;
    }

    setTextAreaValue(editorText);

    datePicker.value = data.execDate.formattedDate;

    return true;
  }

  onDestroy(() => {
    jTextArea.val(editorText = null);
    External.Tools.removeTextEditor(jTextArea);
    contact = null;
    data = null;
    popup = null;
    emailBuilder = null;
    builderContent = null;
    jTextArea = null;
    datePicker = null;
  });

</script>

<div class='popup-widget-wrapper flex flex-col flex-1 height-100 overflow-auto dynamic-popup-content'
     style='padding-top: 0; padding-bottom: 10px;'>
  <div class='col20 f-left small-rules mt-25 flex-1 flex-col justify-center' style='overflow-y: auto;'>
    <div class='col20 f-left'>
      <div class='info'>
        <h5>{M.FROM_SENDER}</h5>
        <span class='light-gray-text'>{M.FROM_DESC}</span>
      </div>
      <input type='text' bind:value={data.name} class='modify transition-border half-input f-left mb-5'
             placeholder={M.NAME}/>
      <input type='text' bind:value={data.fromEmail} class='modify transition-border half-input f-right mb-5'
             placeholder={M.EMAIL} maxlength='254'/>
    </div>
    <div class='col20 f-right mt-5'>
      <a class='text-link-small t-right blue-text' on:click={() => replyToHidden = !replyToHidden}>
        {M.CHANGE_REPLY_TO}
      </a>
    </div>
    <!-- Reply To Area -->
    <div class='col20 f-left mb-15' class:none={replyToHidden}>
      <div class='info'>
        <h5>{M.REPLY_TO}</h5>
        <span class='light-gray-text'>{M.REPLY_TO_DESC}</span>
      </div>
      <input type='text' bind:value={data.replyTo} placeholder={M.REPLIES_RECEIVING_EMAIL} maxlength='254'/>
    </div>
    <!-- Reply To Area END-->
    <div class='col20 f-left'>
      <div class='info'>
        <h5>{M.EMAIL_SUBJECT}</h5>
      </div>
      <div class='popup-input-wrapper'>
        <div class='input-inner-wrapper input-wrapper-with-button'>
          <input type='text' bind:value={data.subject} class='modify transition-border mb-5' placeholder={M.SUBJECT}/>
          <a class='middle-button light-gray-button circle-effect add-email-tags-btn'
             on:click={addProperty}>{M.ADD_PROPERTY}</a>
        </div>
      </div>
    </div>
    <div class='col20 f-left mt-5'>
      {#if builderContent}
        <div class='recipients-summary f-left col20 gray-section flex align-center mb-10'>
          {#key data.thumb}
            <img alt='' class='system-email-box-img mr-15' src={External.content + data.thumb}/>
          {/key}
          <div class='t-center'>
            <a on:click={sendTest}
               class='middle-button f-none inline-block light-gray-button mr-5 mt-5 rounded-effect'>{M.SEND_TEST_EMAIL}</a>
            <a on:click={showBuilder}
               class='middle-button blue f-none inline-block mr-5 mt-5 rounded-effect'>{M.EDIT_EMAIL}</a>
          </div>
        </div>
        <div class='t-center'>
          <a on:click={discardEmail} class='blue-text'>{M.SWITCH_TO_TEXT_EDITOR}</a>
        </div>
      {:else}
        <div use:initTextArea class='rich-text-area f-left col20 mb-10 single-email-textarea'
             style='color: #000; line-height: 23px;'>
          <textarea id='emailTextArea' bind:value={editorText} placeholder={M.TYPE_YOUR_EMAIL}></textarea>
        </div>
        <div class='col20 f-left'>
          <a on:click={showBuilder} class='f-left rounded-effect inline-block single-email-schedule-button'>
            <span class='flex align-center'>
              <svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24'><path
                d='M482-160q-134 0-228-93t-94-227v-7l-64 64-56-56 160-160 160 160-56 56-64-64v7q0 100 70.5 170T482-240q26 0 51-6t49-18l60 60q-38 22-78 33t-82 11Zm278-161L600-481l56-56 64 64v-7q0-100-70.5-170T478-720q-26 0-51 6t-49 18l-60-60q38-22 78-33t82-11q134 0 228 93t94 227v7l64-64 56 56-160 160Z'/></svg>
              {M.SWITCH_TO_EMAIL_BUILDER}
            </span>
          </a>

          {#if !data.emailId}
            <a on:click={() => {data.execDate ? hideDatePicker() : showDatePicker()}} class:blue={!!data.execDate}
               class='f-left rounded-effect inline-block single-email-schedule-button ml-5'>
            <span class='flex align-center'>
              <svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24'><path
                d='M200-640h560v-80H200v80Zm0 0v-80 80Zm0 560q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v227q-19-9-39-15t-41-9v-43H200v400h252q7 22 16.5 42T491-80H200Zm520 40q-83 0-141.5-58.5T520-240q0-83 58.5-141.5T720-440q83 0 141.5 58.5T920-240q0 83-58.5 141.5T720-40Zm67-105 28-28-75-75v-112h-40v128l87 87Z'/></svg>
              {M.SCHEDULE_EMAIL_BTN}
            </span>
            </a>
          {/if}
          <a class='schedule-history-btn f-left ml-5 rounded-effect button-tooltip' on:click={showScheduledEmails}
             data-tooltip='view_scheduled_emails'>
            <svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24'>
              <path
                d='m787-145 28-28-75-75v-112h-40v128l87 87Zm-587 25q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v268q-19-9-39-15.5t-41-9.5v-243H200v560h242q3 22 9.5 42t15.5 38H200Zm0-120v40-560 243-3 280Zm80-40h163q3-21 9.5-41t14.5-39H280v80Zm0-160h244q32-30 71.5-50t84.5-27v-3H280v80Zm0-160h400v-80H280v80ZM720-40q-83 0-141.5-58.5T520-240q0-83 58.5-141.5T720-440q83 0 141.5 58.5T920-240q0 83-58.5 141.5T720-40Z'/>
            </svg>
          </a>
        </div>
      {/if}
    </div>
    {#if data.execDate || data.emailId}
      <div class='col20 f-left mt-15'>
        <div class='info'>
          <h5>{M.SEND_DATE}</h5>
          <span class='light-gray-text'>
            {M.SELECT_SEND_DATE_DESC}
          </span>
        </div>
        <DateTimePicker bind:instance={datePicker} bind:value={data.execDate} onChange='{dateChanged}'
                        readonly={true}/>
      </div>
    {/if}
  </div>
  <div class='f-left contact-email-dialog-footer t-left flex-none'>
    {#if data.emailId || (data.execDate && data.execDate.getTime() > new Date().getTime())}
      <!--existing scheduled email or selected date is in the future-->
      <a on:click={scheduleEmail} class='middle-button f-none inline-block rounded-effect blue'>
        {M.SAVE_EMAIL}
      </a>
    {:else}
      <a on:click={sendNow} class='middle-button f-none inline-block rounded-effect blue'>
        {M.SEND_EMAIL}
      </a>
    {/if}

    {#if data.emailId}
      <a on:click={sendScheduledNow} class='middle-button f-none inline-block rounded-effect gray'>{M.SEND_NOW}</a>
    {/if}

    <a on:click={cancel} class='middle-button f-none inline-block rounded-effect gray ml-5'>{M.CANCEL}</a>
  </div>
</div>
